// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

export const CHECKOUT_STEPS = {
    CART: 0,
    ADDRESS: 1,
    SHIPPING: 2,
    PAYMENT: 3,
    REVIEW: 4,
};

export interface CheckoutStep {
    /** Step Name */
    name: string;
    /** Step Name */
    displayName?: string;
    /** Step index */
    index: number;
    /** Step Next */
    next?: number;
    /** Step Prev */
    prev: number;
}

/**
 * Cases:
 * 1. isPickUp = false & canSubmitForApproval = false then show 4 steps.
 * 2. isPickUp = true & canSubmitForApproval = false then hide Shipping only.
 * 2. isPickUp = false & canSubmitForApproval = true then hide Payment only.
 * 2. isPickUp = true & canSubmitForApproval = true then hide Payment & Shipping.
 * @param isPickUp boolean
 * @param requiresApproval boolean
 * @returns CheckoutStep[]
 */
export const generateSteps = (isPickUp: boolean, requiresApproval?: boolean): CheckoutStep[] => {
    // initialize steps with addresses and review
    const steps: CheckoutStep[] = [
        {
            name: "Addresses",
            prev: CHECKOUT_STEPS.CART,
            index: CHECKOUT_STEPS.ADDRESS,
            next: CHECKOUT_STEPS.REVIEW,
        },
        {
            name: "Review",
            displayName: "Review & Submit Order",
            prev: CHECKOUT_STEPS.ADDRESS,
            index: CHECKOUT_STEPS.REVIEW,
        },
    ];

    steps.splice(1, 0, {
        name: !isPickUp ? "Shipping" : "Pickup",
        displayName: !isPickUp ? "Shipping Information" : "Pickup Information",
        prev: CHECKOUT_STEPS.ADDRESS,
        index: CHECKOUT_STEPS.SHIPPING,
        next: CHECKOUT_STEPS.REVIEW,
    });
    // update addresses step
    findStep(steps, CHECKOUT_STEPS.ADDRESS).next = CHECKOUT_STEPS.SHIPPING;
    // update review step
    findStep(steps, CHECKOUT_STEPS.REVIEW).prev = CHECKOUT_STEPS.SHIPPING;

    if (!requiresApproval) {
        steps.splice(2, 0, {
            name: "Payment",
            displayName: "Payment Information",
            prev: CHECKOUT_STEPS.SHIPPING,
            index: CHECKOUT_STEPS.PAYMENT,
            next: CHECKOUT_STEPS.REVIEW,
        });
        // update shipping
        findStep(steps, CHECKOUT_STEPS.SHIPPING).next = CHECKOUT_STEPS.PAYMENT;
        // update review step
        findStep(steps, CHECKOUT_STEPS.REVIEW).prev = CHECKOUT_STEPS.PAYMENT;
    }

    return steps;
};

export const findStep = (steps: CheckoutStep[], currentStep: number) => {
    return steps.find(item => item.index === currentStep) || steps[0] || {};
};

export const nextStepHandler = (steps: CheckoutStep[], currentStep: number) => {
    const next = findStep(steps, currentStep).next;
    return findStep(steps, next || 1);
};

export const previousStepHandler = (steps: CheckoutStep[], currentStep: number) => {
    const prev = findStep(steps, currentStep).prev;
    return findStep(steps, prev);
};

type CallBackSome<T> = (value: T, index?: number, collection?: T[]) => Promise<boolean>;

export async function some<T>(elements: T[], cb: CallBackSome<T>): Promise<boolean> {
    for (const [index, element] of elements.entries()) {
        if (await cb(element, index, elements)) {
            return true;
        }
    }

    return false;
}

export const CHECKOUT_WHEN_TO_SHIP = {
    ShipWhenAvailable: "Ship When Available",
    ShipWhenComplete: "Ship When Complete",
};
export const CHECKOUT_PICKUP_DAY = {
    Today: "Today",
    Tomorrow: "Tomorrow",
};
